/**
 * SKOP Whale Tracker Page
 *
 * Track whale wallets and see their activity.
 */

import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import {
  getTrackedWallets,
  trackWallet,
  untrackWallet,
  getPopularWhales,
  getWhaleAlerts,
  markAlertsSeen,
  analyzeWallet,
  formatCategory,
  shortenWalletAddress,
  formatUsd,
  formatTimeAgo,
  type TrackedWallet,
  type WalletCategory,
  type WhaleAlert,
  type WalletStats,
  type PopularWhale,
} from '../lib/whales';
import { isValidSolanaAddress } from '../lib/utils';

type Tab = 'tracked' | 'discover' | 'alerts';

export function Whales() {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString() ?? null;

  const [tab, setTab] = useState<Tab>('tracked');
  const [trackedWallets, setTrackedWallets] = useState<TrackedWallet[]>([]);
  const [alerts, setAlerts] = useState<WhaleAlert[]>([]);
  const [popularWhales, setPopularWhales] = useState<PopularWhale[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  // Load data
  useEffect(() => {
    if (walletAddress) {
      setTrackedWallets(getTrackedWallets(walletAddress));
      setAlerts(getWhaleAlerts(walletAddress));
    }
    setPopularWhales(getPopularWhales());
  }, [walletAddress]);

  const handleTrackWallet = (wallet: {
    address: string;
    label: string;
    category: WalletCategory;
  }) => {
    if (!walletAddress) return;
    trackWallet(walletAddress, wallet);
    setTrackedWallets(getTrackedWallets(walletAddress));
    setShowAddModal(false);
  };

  const handleUntrack = (address: string) => {
    if (!walletAddress) return;
    untrackWallet(walletAddress, address);
    setTrackedWallets(getTrackedWallets(walletAddress));
  };

  const handleMarkSeen = () => {
    if (!walletAddress) return;
    const unseenIds = alerts.filter(a => !a.seen).map(a => a.id);
    markAlertsSeen(walletAddress, unseenIds);
    setAlerts(getWhaleAlerts(walletAddress));
  };

  const unseenCount = alerts.filter(a => !a.seen).length;

  if (!walletAddress) {
    return (
      <div style={{
        paddingTop: '48px',
        textAlign: 'center',
        maxWidth: '400px',
        margin: '0 auto',
      }}>
        <div style={{ width: '48px', height: '48px', margin: '0 auto 16px', color: 'var(--accent)' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18"/>
            <path d="M18 17V9"/>
            <path d="M13 17V5"/>
            <path d="M8 17v-3"/>
          </svg>
        </div>
        <h2 style={{ marginBottom: '8px' }}>Whale Tracker</h2>
        <p style={{ color: 'var(--text-tertiary)' }}>
          Connect your wallet to track whale wallets and see their activity.
        </p>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '16px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <span style={{ width: '24px', height: '24px', color: 'var(--accent)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18"/>
              <path d="M18 17V9"/>
              <path d="M13 17V5"/>
              <path d="M8 17v-3"/>
            </svg>
          </span>
          Whale Tracker
        </h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          + Track Wallet
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid var(--border)',
        paddingBottom: '12px',
      }}>
        {([
          { id: 'tracked' as Tab, label: 'Tracked', count: trackedWallets.length },
          { id: 'discover' as Tab, label: 'Discover', count: popularWhales.length },
          { id: 'alerts' as Tab, label: 'Alerts', count: unseenCount },
        ]).map(({ id, label, count }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              padding: '8px 16px',
              background: tab === id ? 'var(--accent-muted)' : 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: tab === id ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {label}
            {count > 0 && (
              <span style={{
                padding: '2px 8px',
                background: tab === id ? 'var(--accent)' : 'var(--bg-tertiary)',
                borderRadius: '12px',
                fontSize: '11px',
                color: tab === id ? '#000' : 'var(--text-tertiary)',
              }}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tracked Wallets Tab */}
      {tab === 'tracked' && (
        <TrackedWalletsTab
          wallets={trackedWallets}
          onUntrack={handleUntrack}
        />
      )}

      {/* Discover Tab */}
      {tab === 'discover' && (
        <DiscoverTab
          popularWhales={popularWhales}
          trackedAddresses={trackedWallets.map(w => w.address)}
          onTrack={handleTrackWallet}
        />
      )}

      {/* Alerts Tab */}
      {tab === 'alerts' && (
        <AlertsTab
          alerts={alerts}
          onMarkSeen={handleMarkSeen}
        />
      )}

      {/* Add Wallet Modal */}
      {showAddModal && (
        <AddWalletModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleTrackWallet}
        />
      )}
    </div>
  );
}

// ============================================================================
// Tracked Wallets Tab
// ============================================================================

function TrackedWalletsTab({
  wallets,
  onUntrack,
}: {
  wallets: TrackedWallet[];
  onUntrack: (address: string) => void;
}) {
  const [expandedWallet, setExpandedWallet] = useState<string | null>(null);
  const [walletStats, setWalletStats] = useState<Record<string, WalletStats>>({});

  const handleExpand = async (address: string) => {
    if (expandedWallet === address) {
      setExpandedWallet(null);
      return;
    }

    setExpandedWallet(address);

    // Fetch stats if not cached
    if (!walletStats[address]) {
      const stats = await analyzeWallet(address);
      if (stats) {
        setWalletStats(prev => ({ ...prev, [address]: stats }));
      }
    }
  };

  if (wallets.length === 0) {
    return (
      <div style={{
        padding: '48px',
        textAlign: 'center',
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <div style={{ width: '32px', height: '32px', margin: '0 auto 12px', color: 'var(--accent)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18"/>
            <path d="M18 17V9"/>
            <path d="M13 17V5"/>
            <path d="M8 17v-3"/>
          </svg>
        </div>
        <h3 style={{ marginBottom: '8px' }}>No wallets tracked</h3>
        <p style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }}>
          Start tracking whale wallets to see their activity
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      {wallets.map((wallet) => {
        const category = formatCategory(wallet.category);
        const isExpanded = expandedWallet === wallet.address;
        const stats = walletStats[wallet.address];

        return (
          <div key={wallet.address}>
            <div
              onClick={() => handleExpand(wallet.address)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
              }}
            >
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '4px',
                }}>
                  <span style={{ fontWeight: 600 }}>{wallet.label}</span>
                  <span style={{
                    padding: '2px 8px',
                    background: `${category.color}20`,
                    borderRadius: 'var(--radius-sm)',
                    color: category.color,
                    fontSize: '11px',
                    fontWeight: 600,
                  }}>
                    {category.label}
                  </span>
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                }}>
                  {shortenWalletAddress(wallet.address, 8)}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  fontSize: '12px',
                  color: 'var(--text-tertiary)',
                }}>
                  {wallet.lastActivity ? formatTimeAgo(wallet.lastActivity) : 'No activity'}
                </span>
                <span style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}>
                  ▼
                </span>
              </div>
            </div>

            {/* Expanded Stats */}
            {isExpanded && (
              <div style={{
                padding: '16px',
                background: 'var(--bg-tertiary)',
                borderBottom: '1px solid var(--border)',
              }}>
                {stats ? (
                  <div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: '16px',
                      marginBottom: '16px',
                    }}>
                      <StatBox label="Win Rate" value={`${stats.winRate.toFixed(1)}%`} />
                      <StatBox label="Total Trades" value={stats.totalTrades.toString()} />
                      <StatBox
                        label="Total P/L"
                        value={formatUsd(stats.totalProfitUsd)}
                        color={stats.totalProfitUsd >= 0 ? 'var(--success)' : 'var(--error)'}
                      />
                      <StatBox label="Last Active" value={formatTimeAgo(stats.lastActive)} />
                    </div>

                    {stats.bestTrade && (
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--text-tertiary)',
                        marginBottom: '8px',
                      }}>
                        Best: <span style={{ color: 'var(--success)' }}>
                          +{stats.bestTrade.profitPercent.toFixed(0)}% on {stats.bestTrade.tokenSymbol}
                        </span>
                      </div>
                    )}

                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '16px',
                    }}>
                      <a
                        href={`https://solscan.io/account/${wallet.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                        style={{ flex: 1, textDecoration: 'none', textAlign: 'center' }}
                      >
                        View on Solscan
                      </a>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUntrack(wallet.address);
                        }}
                        className="btn btn-secondary"
                        style={{ color: 'var(--error)' }}
                      >
                        Untrack
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    <span className="animate-pulse" style={{ color: 'var(--text-tertiary)' }}>
                      Loading stats...
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatBox({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div>
      <div style={{
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        marginBottom: '4px',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '16px',
        fontWeight: 600,
        color: color || 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  );
}

// ============================================================================
// Discover Tab
// ============================================================================

function DiscoverTab({
  popularWhales,
  trackedAddresses,
  onTrack,
}: {
  popularWhales: PopularWhale[];
  trackedAddresses: string[];
  onTrack: (wallet: { address: string; label: string; category: WalletCategory }) => void;
}) {
  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      {popularWhales.map((whale) => {
        const category = formatCategory(whale.category);
        const isTracked = trackedAddresses.includes(whale.address);

        return (
          <div
            key={whale.address}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px',
              }}>
                <span style={{ fontWeight: 600 }}>{whale.label}</span>
                {whale.verified && (
                  <span style={{ color: 'var(--accent)' }} title="Verified">✓</span>
                )}
                <span style={{
                  padding: '2px 8px',
                  background: `${category.color}20`,
                  borderRadius: 'var(--radius-sm)',
                  color: category.color,
                  fontSize: '11px',
                  fontWeight: 600,
                }}>
                  {category.label}
                </span>
              </div>
              <div style={{
                fontSize: '13px',
                color: 'var(--text-tertiary)',
                marginBottom: '4px',
              }}>
                {whale.description}
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-tertiary)',
                fontFamily: 'var(--font-mono)',
              }}>
                {shortenWalletAddress(whale.address, 6)}
                {whale.followers && (
                  <span style={{ marginLeft: '12px' }}>
                    {whale.followers.toLocaleString()} followers
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => onTrack({
                address: whale.address,
                label: whale.label,
                category: whale.category,
              })}
              disabled={isTracked}
              style={{
                padding: '8px 16px',
                background: isTracked ? 'var(--bg-tertiary)' : 'var(--accent)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: isTracked ? 'var(--text-tertiary)' : '#000',
                fontSize: '13px',
                fontWeight: 600,
                cursor: isTracked ? 'default' : 'pointer',
              }}
            >
              {isTracked ? 'Tracking' : 'Track'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Alerts Tab
// ============================================================================

function AlertsTab({
  alerts,
  onMarkSeen,
}: {
  alerts: WhaleAlert[];
  onMarkSeen: () => void;
}) {
  const unseenCount = alerts.filter(a => !a.seen).length;

  if (alerts.length === 0) {
    return (
      <div style={{
        padding: '48px',
        textAlign: 'center',
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <div style={{ width: '32px', height: '32px', margin: '0 auto 12px', color: 'var(--accent)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
        </div>
        <h3 style={{ marginBottom: '8px' }}>No alerts yet</h3>
        <p style={{ color: 'var(--text-tertiary)' }}>
          Alerts will appear when tracked wallets make trades
        </p>
      </div>
    );
  }

  return (
    <div>
      {unseenCount > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '12px',
        }}>
          <button
            onClick={onMarkSeen}
            style={{
              padding: '6px 12px',
              background: 'none',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Mark all as read
          </button>
        </div>
      )}

      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}>
        {alerts.map((alert) => (
          <div
            key={alert.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              borderBottom: '1px solid var(--border)',
              background: alert.seen ? 'transparent' : 'rgba(0, 240, 255, 0.05)',
            }}
          >
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px',
              }}>
                <span style={{ fontWeight: 600 }}>{alert.walletLabel}</span>
                <span style={{
                  padding: '2px 8px',
                  background: alert.activity.type === 'buy'
                    ? 'rgba(0, 255, 136, 0.1)'
                    : 'rgba(255, 59, 59, 0.1)',
                  borderRadius: 'var(--radius-sm)',
                  color: alert.activity.type === 'buy' ? 'var(--success)' : 'var(--error)',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}>
                  {alert.activity.type}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                {alert.activity.amount.toLocaleString()} {alert.activity.tokenSymbol}
                {alert.activity.amountUsd && (
                  <span style={{ color: 'var(--text-tertiary)' }}>
                    {' '}(${alert.activity.amountUsd.toLocaleString()})
                  </span>
                )}
              </div>
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-tertiary)',
              textAlign: 'right',
            }}>
              {formatTimeAgo(alert.createdAt)}
              {!alert.seen && (
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: 'var(--accent)',
                  borderRadius: '50%',
                  marginTop: '4px',
                  marginLeft: 'auto',
                }} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Add Wallet Modal
// ============================================================================

function AddWalletModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (wallet: { address: string; label: string; category: WalletCategory }) => void;
}) {
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<WalletCategory>('custom');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!address || !label) {
      setError('Please fill in all fields');
      return;
    }

    if (!isValidSolanaAddress(address)) {
      setError('Invalid Solana address');
      return;
    }

    onAdd({ address, label, category });
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginBottom: '24px' }}>Track Wallet</h2>

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            marginBottom: '8px',
          }}>
            Wallet Address
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Enter Solana address"
            className="input"
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            marginBottom: '8px',
          }}>
            Label
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g., Smart Money Wallet"
            className="input"
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            marginBottom: '8px',
          }}>
            Category
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {(['whale', 'smart_money', 'dev', 'influencer', 'custom'] as WalletCategory[]).map((cat) => {
              const catInfo = formatCategory(cat);
              return (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  style={{
                    padding: '6px 12px',
                    background: category === cat ? `${catInfo.color}20` : 'var(--bg-tertiary)',
                    border: `1px solid ${category === cat ? catInfo.color : 'var(--border)'}`,
                    borderRadius: 'var(--radius-md)',
                    color: category === cat ? catInfo.color : 'var(--text-secondary)',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  {catInfo.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            background: 'rgba(255, 59, 59, 0.1)',
            border: '1px solid rgba(255, 59, 59, 0.3)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--error)',
            fontSize: '13px',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            className="btn btn-secondary"
            style={{ flex: 1 }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary"
            style={{ flex: 1 }}
          >
            Track
          </button>
        </div>
      </div>
    </div>
  );
}
